// Global Instructions Rule Applied!

import React from 'react'
import { Alert, Col, Row, Spin } from 'antd'
import { useDispatch } from 'react-redux'
import { FormSection } from '../../../../../core/components/FormSection.tsx'
import { InputManager } from '../../../../../core/components/InputManager/index.ts'
import { useFundTerms } from '../../../hooks/useFundTerms.ts'
import { useFundTermsFieldOptions } from '../../../hooks/useFundTermsFieldOptions.ts'
import { updateFundRecord } from '../../../../../state_manager/state_update_actions/funds/index.ts'
import { updateActiveRecord } from '../../../../../state_manager/state_update_actions/active_records/active_record_actions.ts'
import { updateGlobalConfigFund } from '../../../../../state_manager/state_update_actions/global_configs/index.ts'
import {
  getDetailsFields,
  getKeyDatesFields,
  getFinalClosingFields,
  getInvestmentPeriodFields,
  getFundTermFields,
  getCommitmentFields,
  getManagementFeeFields,
  getCarriedInterestFields,
  getAllocationBasisFields,
  getFinalClosingComputedFields,
  getInvestmentPeriodComputedFields,
  getFundTermComputedFields,
  getManagementFeeComputedFields,
  getFundraisingFields,
  getManagerFields,
  getStatusFields,
  getLegalEntityFields,
  getCapitalCallTermsFields,
  getNoticeSenderFields
} from '../../../utils/fundTermsFieldConfigs.ts'
import { fundCarryDefaultsOf } from '../../../../../core/utils/fundCarryDefaults.ts'
import FeePeriodsEditor from './FeePeriodsEditor.tsx'
import WaterfallSection from './WaterfallSection.tsx'
import NoticeSettingsSections from './NoticeSettingsSections.tsx'
import StatementSettingsSection from './StatementSettingsSection.tsx'

// One shared label-column width across every section: each InputManager
// otherwise sizes its label column to its own longest label, so the value
// columns of vertically stacked sections drift out of alignment.
const LABEL_WIDTH = 235

interface TermsTabProps {
  fundId: string | null
}

export default function TermsTab({ fundId }: TermsTabProps) {
  const dispatch = useDispatch()
  const { terms, loading, error, refetch } = useFundTerms(fundId)
  const { fieldOptions, fundStatuses } = useFundTermsFieldOptions(fundId)

  const handlePatchSuccess = (recordId: string | number, updates: Record<string, any>) => {
    if (recordId) {
      dispatch(updateFundRecord({ fundId: String(recordId), updates }))
      // Identity edits: the tab title reads activeRecords first, and every
      // fund picker reads globalConfigs.funds — keep both current.
      const identityUpdate: Record<string, any> = {}
      ;(['name', 'legal_name', 'functional_currency_id', 'managed_by_us', 'is_sbic'] as const).forEach((col) => {
        const v = updates[`funds__${col}`] ?? updates[col]
        if (v !== undefined) identityUpdate[col] = v
      })
      if (Object.keys(identityUpdate).length > 0) {
        dispatch(updateGlobalConfigFund(String(recordId), identityUpdate))
        if ('name' in identityUpdate) {
          dispatch(updateActiveRecord({ uuid: String(recordId), updates: { funds__name: identityUpdate.name } }))
        }
      }
      // The kanban / drawer / Bulk Add Prospects derive stage defaults from
      // globalConfigs.funds[].pipeline_origination — keep it in sync so the
      // edit takes effect without a full configs reload. (The backend's own
      // AppState cache still needs a restart to see it server-side.)
      // The fund pickers gate on globalConfigs.funds[].is_active /
      // is_fundraising_open, both computed server-side. Recompute them here
      // from the catalogue, or every picker (and the Dashboard Tag) keeps the
      // pre-edit answer for the rest of the session.
      // A CLEARED status arrives as a present key with no value, so test for
      // the key - reading the value alone would treat a clear as "this save
      // did not touch the status" and leave the old flags in place.
      const statusUpdate: Record<string, any> = {}
      const touched = (col: string) => `funds__${col}` in updates || col in updates
      const nextFundraising = touched('fundraising_status_id')
        ? (updates['funds__fundraising_status_id'] ?? updates['fundraising_status_id'] ?? null)
        : undefined
      const nextInvestment = touched('investment_status_id')
        ? (updates['funds__investment_status_id'] ?? updates['investment_status_id'] ?? null)
        : undefined
      if (nextFundraising !== undefined) {
        const row = fundStatuses.find((s: any) => s.id === nextFundraising)
        statusUpdate.fundraising_status_id = nextFundraising ?? null
        // Fail-open on an unclassified fund, exactly as the RPC does.
        statusUpdate.is_fundraising_open = row ? !!row.is_open : true
      }
      if (nextInvestment !== undefined) {
        const row = fundStatuses.find((s: any) => s.id === nextInvestment)
        statusUpdate.investment_status_id = nextInvestment ?? null
      }
      if (Object.keys(statusUpdate).length > 0) {
        // a status cleared in this save is null here and must not fall back
        // to the value the tab still holds from before the save
        const frId =
          'fundraising_status_id' in statusUpdate
            ? statusUpdate.fundraising_status_id
            : (terms?.funds__fundraising_status_id ?? null)
        const invId =
          'investment_status_id' in statusUpdate
            ? statusUpdate.investment_status_id
            : (terms?.funds__investment_status_id ?? null)
        const frRow = fundStatuses.find((s: any) => s.id === frId)
        const invRow = fundStatuses.find((s: any) => s.id === invId)
        statusUpdate.is_active = !(frRow?.is_terminal === true) && !(invRow?.is_terminal === true)
        dispatch(updateGlobalConfigFund(String(recordId), statusUpdate))
      }
      if ('funds__pipeline_origination' in updates || 'pipeline_origination' in updates) {
        dispatch(
          updateGlobalConfigFund(String(recordId), {
            pipeline_origination:
              updates['funds__pipeline_origination'] ?? updates['pipeline_origination'] ?? null
          })
        )
      }
    }
    // Derived effective dates depend on the saved values - re-resolve server-side.
    refetch()
  }

  if (!fundId) {
    return (
      <div className='border border-dashed border-gray-300 rounded-lg p-8 m-3 text-center text-gray-500'>
        Save the fund before editing terms.
      </div>
    )
  }

  if (loading && !terms) {
    return (
      <div className='flex justify-center items-center h-64'>
        <Spin />
      </div>
    )
  }

  if (error && !terms) {
    return <Alert type='error' message='Failed to load fund terms' description={error} className='m-3' showIcon />
  }

  if (!terms) return null

  const inputManagerProps = {
    inputMode: 'read' as const,
    labelPosition: 'inline' as const,
    enableAutoPatch: true,
    patchId: fundId,
    recordId: fundId,
    currentRecord: terms as Record<string, any>,
    onPatchSuccess: handlePatchSuccess,
    fieldWrapping: true,
    maxLabelWidth: LABEL_WIDTH
  }

  return (
    <div className='h-full overflow-y-auto p-3 bg-gray-50'>
      {terms.prog__effective_dates_error ? (
        <Alert
          type='warning'
          className='mb-3'
          showIcon
          message='Effective dates could not be resolved'
          description={terms.prog__effective_dates_error}
        />
      ) : null}

      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <FormSection title='Details' titleColor='blue'>
            <InputManager {...inputManagerProps} fields={getDetailsFields(terms, fieldOptions)} />
          </FormSection>
        </Col>
        <Col xs={24}>
          <FormSection title='Status' titleColor='blue'>
            <InputManager {...inputManagerProps} fields={getStatusFields(terms, fieldOptions)} />
            {terms.prog__fundraising_status_drift || terms.prog__investment_status_drift ? (
              <div className='mt-2 pt-2 border-t border-gray-100'>
                <Alert
                  type='info'
                  showIcon
                  message='The configured dates suggest a different status'
                  description={
                    <>
                      {terms.prog__fundraising_status_drift
                        ? 'The fundraising clock looks out of step with the closing dates below. '
                        : ''}
                      {terms.prog__investment_status_drift
                        ? 'The investing clock looks out of step with the investment-period dates below. '
                        : ''}
                      The status is yours to set — this is only a hint, and nothing changes it for you.
                    </>
                  }
                />
              </div>
            ) : null}
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Key Dates' titleColor='blue'>
            <InputManager {...inputManagerProps} fields={getKeyDatesFields(terms)} />
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Final Closing' titleColor='green'>
            <InputManager {...inputManagerProps} fields={getFinalClosingFields(terms, fieldOptions)} />
            <div className='mt-2 pt-2 border-t border-gray-100'>
              <InputManager {...inputManagerProps} fields={getFinalClosingComputedFields(terms)} />
            </div>
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Investment Period' titleColor='green'>
            <InputManager {...inputManagerProps} fields={getInvestmentPeriodFields(terms, fieldOptions)} />
            <div className='mt-2 pt-2 border-t border-gray-100'>
              <InputManager {...inputManagerProps} fields={getInvestmentPeriodComputedFields(terms)} />
            </div>
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Fund Term' titleColor='green'>
            <InputManager {...inputManagerProps} fields={getFundTermFields(terms, fieldOptions)} />
            <div className='mt-2 pt-2 border-t border-gray-100'>
              <InputManager {...inputManagerProps} fields={getFundTermComputedFields(terms)} />
            </div>
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Commitments' titleColor='purple'>
            <InputManager {...inputManagerProps} fields={getCommitmentFields(terms, fieldOptions)} />
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Fundraising' titleColor='blue'>
            <InputManager {...inputManagerProps} fields={getFundraisingFields(terms, fieldOptions)} />
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Management Fees' titleColor='orange'>
            <InputManager {...inputManagerProps} fields={getManagementFeeFields(terms, fieldOptions)} />
            <div className='mt-2 pt-2 border-t border-gray-100'>
              <InputManager {...inputManagerProps} fields={getManagementFeeComputedFields(terms, fieldOptions)} />
              {terms.funds__is_sbic && (terms.prog__sbic_uncategorised_investors ?? 0) > 0 ? (
                <Alert
                  type='warning'
                  showIcon
                  className='mt-2'
                  message={`${terms.prog__sbic_uncategorised_investors} investor(s) have no SBIC investor type and are counted as non-institutional (paid-in only). Set it via Investors ▸ Edit Investor.`}
                />
              ) : null}
            </div>
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Manager' titleColor='purple'>
            <InputManager {...inputManagerProps} fields={getManagerFields(terms, fieldOptions)} />
          </FormSection>
        </Col>
        {/* Carried interest: the LPA's terms, the default every commitment
            without a side-letter term inherits (the Commitment form's Terms
            section overrides them per investor). */}
        <Col xs={24} xl={12}>
          <FormSection title='Carried Interest' titleColor='purple'>
            <InputManager {...inputManagerProps} fields={getCarriedInterestFields(terms)} />
          </FormSection>
        </Col>
        {/* Partners' share of fund results: the allocation basis of the fund's
            unstamped results (docs/LATE_CLOSER_PNL_ALLOCATION_DESIGN.md).
            Read-only: set by the scripted conversion of a fund, never here. */}
        <Col xs={24} xl={12}>
          <FormSection title="Partners' Share of Fund Results" titleColor='purple'>
            <InputManager {...inputManagerProps} fields={getAllocationBasisFields(terms)} />
          </FormSection>
        </Col>
        <Col xs={24}>
          <FormSection title='Fee Periods' titleColor='orange'>
            {fundId ? <FeePeriodsEditor fundId={fundId} terms={terms} onSaved={refetch} /> : null}
          </FormSection>
        </Col>
        {/* Distribution waterfall: the tiers carry pays through, by shape; each
            LP's hurdle and carry rate come from its commitment terms, the
            fund's Carried Interest terms where the commitment has none. */}
        {fundId ? <WaterfallSection fundId={fundId} fundDefaults={fundCarryDefaultsOf(terms)} /> : null}
        {/* Capital call notices: what the LPA says about calls, who sends the
            notices, where they are paid and how they read. */}
        <Col xs={24} xl={12}>
          <FormSection title='General Partner & Address' titleColor='blue'>
            <InputManager {...inputManagerProps} fields={getLegalEntityFields(terms)} />
          </FormSection>
        </Col>
        <Col xs={24} xl={12}>
          <FormSection title='Capital Call Terms' titleColor='purple'>
            <InputManager {...inputManagerProps} fields={getCapitalCallTermsFields(terms)} />
            <div className='mt-2 pt-2 border-t border-gray-100'>
              <InputManager {...inputManagerProps} fields={getNoticeSenderFields(terms)} />
            </div>
          </FormSection>
        </Col>
        {fundId ? <NoticeSettingsSections fundId={fundId} maxLabelWidth={LABEL_WIDTH} /> : null}
        {/* Investor statements: the layout and the wording of the quarterly
            statements (what they print about the fund are the terms above). */}
        {fundId ? <StatementSettingsSection fundId={fundId} maxLabelWidth={LABEL_WIDTH} /> : null}
      </Row>
    </div>
  )
}
